import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ENTRY_DEFAULTS_SAVE_DEBOUNCE_MS,
  HAVE_LIST_STOCK_DEFAULTS,
  HaveEntryDefaults,
  WANT_LIST_STOCK_DEFAULTS,
  WantEntryDefaults,
} from 'constants/entryDefaults';
import {
  useGetHaveListDefaultsQuery,
  useGetWantListDefaultsQuery,
  useUpdateHaveListDefaultsMutation,
  useUpdateWantListDefaultsMutation,
} from 'store/services/settings/settingsApi';
import {
  EntrySummary,
  haveEntryDefaultsFromServer,
  haveEntryDefaultsToServer,
  isHaveStock,
  isWantStock,
  summarizeHaveDefaults,
  summarizeWantDefaults,
  wantEntryDefaultsFromServer,
  wantEntryDefaultsToServer,
} from 'utils/entryDefaults';

export interface EntryDefaultsApi<T> {
  /** Current values: the saved ones with any not-yet-saved change on top. */
  values: T;
  /** The same values, readable from a handler without waiting for a render. */
  getCurrent: () => T;
  /** Apply a change now and save it after the debounce. */
  setValues: (patch: Partial<T>) => void;
  /** Back to the list's stock defaults, saved immediately. */
  reset: () => void;
  isStock: boolean;
  summary: EntrySummary;
}

/**
 * Local-first editing over a server-backed defaults record: a change shows at
 * once, the write is debounced, and a failed write drops the local change (the
 * mutation undoes its own cache patch) and says so.
 */
function useDebouncedDefaults<T extends object>(
  saved: T,
  save: (patch: Partial<T>) => Promise<unknown>,
): Pick<EntryDefaultsApi<T>, 'values' | 'getCurrent' | 'setValues'> & { saveNow: (patch: Partial<T>) => void } {
  const [pending, setPending] = useState<Partial<T> | null>(null);
  const pendingRef = useRef<Partial<T> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const values = useMemo(() => ({ ...saved, ...(pending ?? {}) }), [saved, pending]);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const patch = pendingRef.current;
    if (!patch) return;
    saveRef.current(patch)
      .catch(() => {
        toast.error('Failed to save your defaults. Please try again.');
      })
      .finally(() => {
        // A newer change made while this one was in flight stays pending.
        if (pendingRef.current === patch) {
          pendingRef.current = null;
          setPending(null);
        }
      });
  }, []);

  const apply = useCallback((patch: Partial<T>) => {
    const next = { ...(pendingRef.current ?? {}), ...patch };
    pendingRef.current = next;
    // Updated here, not only on render, so an add fired in the same tick reads it.
    valuesRef.current = { ...valuesRef.current, ...patch };
    setPending(next);
  }, []);

  const setValues = useCallback(
    (patch: Partial<T>) => {
      apply(patch);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, ENTRY_DEFAULTS_SAVE_DEBOUNCE_MS);
    },
    [apply, flush],
  );

  const saveNow = useCallback(
    (patch: Partial<T>) => {
      apply(patch);
      flush();
    },
    [apply, flush],
  );

  // Leaving the page must not drop a change still inside the debounce window.
  useEffect(() => flush, [flush]);

  const getCurrent = useCallback(() => valuesRef.current, []);

  return { values, getCurrent, setValues, saveNow };
}

export const useHaveEntryDefaults = (): EntryDefaultsApi<HaveEntryDefaults> => {
  const { data } = useGetHaveListDefaultsQuery();
  const [update] = useUpdateHaveListDefaultsMutation();
  const saved = useMemo(
    () => haveEntryDefaultsFromServer(data),
    // Only the three entry fields matter; alert counters churn independently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.defaultEntryCondition, data?.defaultEntryFinish, data?.defaultEntryLanguage],
  );
  const { values, getCurrent, setValues, saveNow } = useDebouncedDefaults<HaveEntryDefaults>(
    saved,
    (patch) => update(haveEntryDefaultsToServer(patch)).unwrap(),
  );
  const reset = useCallback(() => saveNow(HAVE_LIST_STOCK_DEFAULTS), [saveNow]);
  return {
    values,
    getCurrent,
    setValues,
    reset,
    isStock: isHaveStock(values),
    summary: summarizeHaveDefaults(values),
  };
};

/** The Want List version defaults themselves: same record Settings edits. */
export const useWantEntryDefaults = (): EntryDefaultsApi<WantEntryDefaults> => {
  const { data } = useGetWantListDefaultsQuery();
  const [update] = useUpdateWantListDefaultsMutation();
  const saved = useMemo(() => wantEntryDefaultsFromServer(data), [data]);
  const { values, getCurrent, setValues, saveNow } = useDebouncedDefaults<WantEntryDefaults>(
    saved,
    (patch) => update(wantEntryDefaultsToServer(patch)).unwrap(),
  );
  const reset = useCallback(() => saveNow(WANT_LIST_STOCK_DEFAULTS), [saveNow]);
  return {
    values,
    getCurrent,
    setValues,
    reset,
    isStock: isWantStock(values),
    summary: summarizeWantDefaults(values),
  };
};
